/** Domain types aligned with `lib/models/app_models.dart`. */

export type CompanyRole = 'owner' | 'supervisor' | 'member' | 'viewer';

export interface Company {
  id: string;
  name: string;
  businessName?: string;
  colorHex: number;
  joinCode?: string;
  logoUrl?: string;
  description?: string;
  backgroundAsset?: string;
  autoSelectSupervisorsOnNewTasks: boolean;
  allowChatMessageDelete: boolean;
  allowChatMessageEdit: boolean;
  chatMessageDeleteWindowMinutes: number;
  chatMessageEditWindowMinutes: number;
  /**
   * Web-only quick filter capsule ids (max 10): toggle field names or `tag:…`.
   */
  quickFilterIds?: string[];
  billingPlan?: string;
  /** UTC instant complimentary Premium ends (see `company_billing_plan.dart`). */
  premiumComplimentaryEndsAt?: Date;
  /** UTC instant paid Premium is valid through. */
  premiumSubscriptionPaidThrough?: Date;
  createdAt: Date;
  createdBy: string;
}

export interface Member {
  uid: string;
  role: CompanyRole;
  displayName?: string;
  phoneNumber?: string;
  joinedAt: Date;
  backgroundAsset?: string;
}

export type TaskStatus = 'open' | 'closed';

export interface Task {
  id: string;
  companyId: string;
  title: string;
  description?: string;
  tags: string[];
  createdBy: string;
  createdAt: Date;
  lastMessageAt?: Date;
  eta?: Date;
  repeat?: string;
  repeatTime?: string;
  repeatEtaTime?: string;
  taskNumber?: number;
  taskDisplayCode?: string;
  status: TaskStatus;
  closedBy?: string;
  closedAt?: Date;
  assignees: string[];
  primaryAssigneeId?: string;
  supervisors: string[];
  hasClosureRequest: boolean;
  /** When a closure request is pending — from `closureRequest.requestedAt`. */
  closureRequestedAt?: Date;
  /** From `lastClosureRequest.requestedAt` (pending / rejected / accepted). */
  lastClosureRequestedAt?: Date;
  lastClosureRequestStatus?: string;
  closedWithoutClosureRequest: boolean;
  closedAsNotDone: boolean;
  isBlocked: boolean;
  isEtaIncreaseRequested: boolean;
  /** Closed-task appreciations: uid → emoji (👍 👏 🎉 🚀). */
  appreciations: Record<string, string>;
  /** Soft-delete timestamp; absent means active. */
  deletedAt?: Date;
  deletedBy?: string;
  /**
   * UIDs @mentioned in this task's chat (denormalized; may lag until backfilled).
   * Filter also uses a live collection-group query of message mentions.
   */
  mentionedUserIds: string[];
}

/** Soft-deleted. `deletedAt` may be null while the server timestamp is pending. */
export function isTaskSoftDeleted(
  task: Pick<Task, 'deletedAt' | 'deletedBy'>,
): boolean {
  return task.deletedAt != null || Boolean(task.deletedBy?.trim());
}

export interface TaskMessage {
  id: string;
  senderId: string;
  text: string;
  createdAt: Date;
  type: string;
  attachmentName?: string;
  /** When `markdown`, [text] is rendered with lists and emphasis. */
  textFormat?: 'plain' | 'markdown';
  fieldCheckIn?: FieldCheckInMessageData;
  editedAt?: Date;
  deletedAt?: Date;
  /** Uids @mentioned in this message (when stored). */
  mentionedUserIds?: string[];
}

export interface FieldCheckInMessageData {
  checkInId: string;
  sessionId: string;
  photoUrls: string[];
  lat: number;
  lng: number;
  userName?: string;
}

export function roleFromFirestore(v: string | undefined): CompanyRole {
  switch ((v ?? '').toLowerCase()) {
    case 'owner':
      return 'owner';
    case 'supervisor':
      return 'supervisor';
    case 'viewer':
      return 'member';
    default:
      return 'member';
  }
}

export const personalWorkspacePrefix = 'personal_';

export function isPersonalWorkspaceId(id: string | null | undefined): boolean {
  return !!id && id.startsWith(personalWorkspacePrefix);
}

export function personalWorkspaceIdForUser(uid: string): string {
  return `${personalWorkspacePrefix}${uid}`;
}
